import React, { useMemo } from 'react';
import { User, Calendar, MessageCircle, PlayCircle } from 'lucide-react';
import GroupsList from '../../components/GroupsList';
import ArticlesSearchBlock from '../../components/ArticlesSearchBlock';
import { formatUserDisplayName } from '../../utils/userDisplayName';
import '../MainPage/MainPage.css';
import '../ArticlesPage/ArticlesPage.css';
import './NaslediePublicProfileLanding.css';

const scrollToId = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/**
 * Лендинг-профиль преподавателя/психолога для Наследия (iframe).
 * Только два цвета: зелёный (#657664) и белый.
 * Статьи — под группами, тот же блок что на /public/articles.
 */
const NaslediePublicProfileLanding = ({
    profile,
    headerShort,
    aboutText,
    educationText,
    qualifications,
    userArticles,
    allGroups,
    groups,
    displayDirections,
    selectedDirection,
    selectedRegion,
    onDirectionClick,
    onRegionClick,
    onBook,
    onAsk,
    onOpenVideo
}) => {
    const displayName = formatUserDisplayName(profile);
    const hasServices = profile.therapy_topics && profile.therapy_topics.length > 0;
    const hasPrices =
        profile.price ||
        (profile.topic_prices && Object.keys(profile.topic_prices).length > 0);
    const hasServicesBlock = hasServices || hasPrices;
    const hasDetails =
        qualifications.length > 0 ||
        (profile.city && profile.city.length > 0) ||
        (profile.language && profile.language.length > 0) ||
        (profile.work_format && profile.work_format.length > 0);
    const hasAbout = Boolean(aboutText?.trim()) || Boolean(educationText);
    const hasGroups = allGroups && allGroups.length > 0;
    const hasArticles = userArticles && userArticles.length > 0;
    const hasCerts = profile.certificates && profile.certificates.length > 0;
    const canBook = Boolean(profile.is_therapist);

    // Контентные секции до групп (статьи — отдельно, после групп)
    const sectionKeys = useMemo(() => {
        const keys = [];
        if (hasAbout) keys.push('about');
        if (hasServicesBlock) keys.push('services');
        if (hasDetails) keys.push('details');
        if (hasCerts) keys.push('certs');
        return keys;
    }, [hasAbout, hasServicesBlock, hasDetails, hasCerts]);

    // Только green / white. Герой — green; группы и статьи — white;
    // блок перед группами — green, если иначе был бы white.
    const sectionTone = useMemo(() => {
        const cycle = ['green', 'white'];
        const keys = [
            'hero',
            ...sectionKeys,
            ...(hasGroups ? ['groups'] : []),
            ...(hasArticles ? ['articles'] : [])
        ];
        const map = {};

        keys.forEach((key, i) => {
            map[key] = cycle[i % cycle.length];
        });

        map.hero = 'green';
        if (hasGroups) {
            map.groups = 'white';
            const groupIndex = keys.indexOf('groups');
            if (groupIndex > 0) {
                const before = keys[groupIndex - 1];
                if (map[before] === 'white') {
                    map[before] = 'green';
                }
            }
        }
        if (hasArticles) {
            map.articles = 'white';
        }

        return map;
    }, [sectionKeys, hasGroups, hasArticles]);

    const renderBookBtn = () =>
        canBook ? (
            <button type="button" className="np-btn np-btn-primary np-btn-inline" onClick={onBook}>
                <Calendar size={18} />
                Записаться на консультацию
            </button>
        ) : null;

    return (
        <div className="nasledie-profile-landing">
            <section className={`np-section np-section--${sectionTone.hero}`} id="np-hero">
                <div className="np-hero-inner">
                    {profile.photo_url ? (
                        <img
                            src={profile.photo_url}
                            alt={displayName}
                            className="np-hero-photo"
                        />
                    ) : (
                        <div className="np-hero-photo np-hero-photo--placeholder">
                            <User size={72} />
                        </div>
                    )}
                    <h1 className="np-hero-name">{displayName}</h1>
                    {headerShort ? (
                        <p className="np-hero-short">{headerShort}</p>
                    ) : null}

                    <div className="np-hero-actions">
                        {canBook && (
                            <button type="button" className="np-btn np-btn-primary" onClick={onBook}>
                                <Calendar size={18} />
                                Записаться на консультацию
                            </button>
                        )}
                        <button type="button" className="np-btn np-btn-secondary" onClick={onAsk}>
                            <MessageCircle size={18} />
                            Задать вопрос
                        </button>
                        {profile.video_card_url && (
                            <button type="button" className="np-btn np-btn-secondary" onClick={onOpenVideo}>
                                <PlayCircle size={18} />
                                Видеовизитка
                            </button>
                        )}
                        {hasServicesBlock && (
                            <button
                                type="button"
                                className="np-btn np-btn-secondary"
                                onClick={() => scrollToId('np-services')}
                            >
                                Услуги
                            </button>
                        )}
                        {hasGroups && (
                            <button
                                type="button"
                                className="np-btn np-btn-secondary"
                                onClick={() => scrollToId('np-groups')}
                            >
                                Группы
                            </button>
                        )}
                        {hasArticles && (
                            <button
                                type="button"
                                className="np-btn np-btn-secondary"
                                onClick={() => scrollToId('np-articles')}
                            >
                                Статьи
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {hasAbout && (
                <section className={`np-section np-section--${sectionTone.about}`} id="np-about">
                    <div className="np-section-inner">
                        <h2 className="np-section-title">О себе</h2>
                        {aboutText?.trim() ? (
                            <div className="np-section-text">
                                {aboutText.split('\n').map((line, idx) => (
                                    <p key={idx}>{line || '\u00A0'}</p>
                                ))}
                            </div>
                        ) : null}
                        {educationText ? (
                            <>
                                <h3 className="np-section-subtitle">Образование</h3>
                                <div className="np-section-text">
                                    {educationText.split('\n').map((line, idx) => (
                                        <p key={idx}>{line || '\u00A0'}</p>
                                    ))}
                                </div>
                            </>
                        ) : null}
                    </div>
                </section>
            )}

            {hasServicesBlock && (
                <section className={`np-section np-section--${sectionTone.services}`} id="np-services">
                    <div className="np-section-inner">
                        {hasServices && (
                            <>
                                <h2 className="np-section-title">Услуги</h2>
                                <div className="np-tags">
                                    {profile.therapy_topics.map((topic, idx) => (
                                        <span key={idx} className="np-tag">{topic}</span>
                                    ))}
                                </div>
                            </>
                        )}
                        {hasPrices && (
                            <>
                                <h2 className={`np-section-title${hasServices ? ' np-section-title--nested' : ''}`}>
                                    Стоимость консультации
                                </h2>
                                <div className="np-prices">
                                    {profile.price != null && profile.price !== '' && (
                                        <div className="np-price-row">
                                            <span>Базовая цена</span>
                                            <strong>{Number(profile.price).toLocaleString('ru-RU')} ₽</strong>
                                        </div>
                                    )}
                                    {profile.topic_prices &&
                                        Object.entries(profile.topic_prices).map(([topic, price]) => (
                                            <div key={topic} className="np-price-row">
                                                <span>{topic}</span>
                                                <strong>{Number(price).toLocaleString('ru-RU')} ₽</strong>
                                            </div>
                                        ))}
                                </div>
                            </>
                        )}
                        {canBook && <div className="np-section-cta">{renderBookBtn()}</div>}
                    </div>
                </section>
            )}

            {hasDetails && (
                <section className={`np-section np-section--${sectionTone.details}`} id="np-details">
                    <div className="np-section-inner">
                        <h2 className="np-section-title">О специалисте</h2>
                        {qualifications.length > 0 && (
                            <div className="np-detail-block">
                                <h3 className="np-section-subtitle">Квалификация</h3>
                                <div className="np-tags">
                                    {qualifications.map((q, idx) => (
                                        <span key={idx} className="np-tag">{q}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {profile.city?.length > 0 && (
                            <div className="np-detail-block">
                                <h3 className="np-section-subtitle">Города</h3>
                                <div className="np-tags">
                                    {profile.city.map((city, idx) => (
                                        <span key={idx} className="np-tag">{city}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {profile.language?.length > 0 && (
                            <div className="np-detail-block">
                                <h3 className="np-section-subtitle">Языки</h3>
                                <div className="np-tags">
                                    {profile.language.map((lang, idx) => (
                                        <span key={idx} className="np-tag">{lang}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {profile.work_format?.length > 0 && (
                            <div className="np-detail-block">
                                <h3 className="np-section-subtitle">Форматы</h3>
                                <div className="np-tags">
                                    {profile.work_format.map((format, idx) => (
                                        <span key={idx} className="np-tag">{format}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {hasCerts && (
                <section className={`np-section np-section--${sectionTone.certs}`} id="np-certs">
                    <div className="np-section-inner">
                        <h2 className="np-section-title">Сертификаты</h2>
                        <div className="np-certs-grid">
                            {profile.certificates.map((cert, index) => {
                                const url = cert.startsWith('/uploads/')
                                    ? `${window.location.origin}${cert}`
                                    : cert;
                                return (
                                    <button
                                        key={index}
                                        type="button"
                                        className="np-cert-item"
                                        onClick={() => window.open(url, '_blank')}
                                    >
                                        <img src={url} alt={`Сертификат ${index + 1}`} />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {hasGroups && (
                <section className={`np-section np-section--${sectionTone.groups}`} id="np-groups">
                    <div className="np-section-inner">
                        <h2 className="np-section-title">Группы</h2>
                        <div className="main-page">
                            <div className="filter-buttons">
                                <h4>Какая группа вас интересует?</h4>
                                <br />
                                <div className="button-row">
                                    {displayDirections?.map((direction) => (
                                        <button
                                            key={direction.id}
                                            className={selectedDirection === direction.id ? 'active-filter' : ''}
                                            onClick={() => onDirectionClick(direction.id)}
                                        >
                                            {direction.name}
                                        </button>
                                    ))}
                                </div>
                                <br />

                                <h4>Место проведения группы:</h4>
                                <br />
                                <div className="button-row">
                                    <button
                                        className={selectedRegion === 'Москва' ? 'active-filter' : ''}
                                        onClick={() => onRegionClick('Москва')}
                                    >
                                        Москва
                                    </button>
                                    <button
                                        className={selectedRegion === 'Онлайн' ? 'active-filter' : ''}
                                        onClick={() => onRegionClick('Онлайн')}
                                    >
                                        Онлайн
                                    </button>
                                    <button
                                        className={selectedRegion === 'Регионы' ? 'active-filter' : ''}
                                        onClick={() => onRegionClick('Регионы')}
                                    >
                                        Регионы
                                    </button>
                                </div>
                                <br />
                                <hr width="100%" />
                                <br />
                            </div>

                            <div>
                                <GroupsList groups={groups} orgCode="nasledie" />
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {hasArticles && (
                <section className={`np-section np-section--${sectionTone.articles}`} id="np-articles">
                    <div className="np-section-inner">
                        <h2 className="np-section-title">Статьи</h2>
                        <ArticlesSearchBlock
                            articles={userArticles}
                            className="np-articles-search"
                        />
                    </div>
                </section>
            )}
        </div>
    );
};

export default NaslediePublicProfileLanding;
